import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of, from } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface OAuthProvider {
  name: string;
  display_name: string;
  login_url: string;
  enabled: boolean;
  scopes: string[];
  color: string;
  icon: string;
}

export interface AppConfig {
  features: {
    qrOrdering: boolean;
    deliveryEnabled: boolean;
    reviewsEnabled: boolean;
    notesEnabled: boolean;
  };
  limits: {
    maxSearchRadius: number;
    maxFileSize: number;
    maxImagesPerReview: number;
  };
  contact: {
    supportEmail: string;
    supportPhone: string;
  };
  oauth: {
    enabled: boolean;
    providers: OAuthProvider[];
  };
  registration: {
    enabled: boolean;
    email_verification_required: boolean;
    password_requirements: {
      min_length: number;
      require_uppercase: boolean;
      require_lowercase: boolean;
      require_numbers: boolean;
      require_special: boolean;
    };
  };
  login: {
    rate_limiting_enabled: boolean;
    session_management: boolean;
  };
  turnstile?: {
    enabled: boolean;
    siteKey: string;
  };
  mapbox?: {
    enabled: boolean;
    accessToken: string;
  };
  media?: {
    baseUrl: string;
  };
}

export interface ConfigResponse {
  success: boolean;
  config: AppConfig;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private readonly API_URL = '/api/v1/auth/config';
  private configSubject = new BehaviorSubject<AppConfig | null>(null);
  public config$ = this.configSubject.asObservable();
  private loadingPromise: Promise<ConfigResponse> | null = null;

  constructor(private http: HttpClient) {
    // Cargar configuración automáticamente al inicializar el servicio
    this.loadConfigSingleton();
  }

  /**
   * Cargar configuración una sola vez (singleton pattern)
   */
  private loadConfigSingleton(): Promise<ConfigResponse> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    console.log('🔧 ConfigService: Cargando configuración por primera vez...');

    this.loadingPromise = this.http.get<ConfigResponse>(this.API_URL)
      .pipe(
        tap(response => {
          if (response.success && response.config) {
            this.configSubject.next(response.config);
          }
        }),
        catchError(error => {
          console.error('Error cargando configuración desde backend:', error);
          console.error('Status:', error.status, 'URL:', error.url);
          console.error('Error details:', error.error);
          // Devolver configuración por defecto solo como fallback
          const defaultConfig: AppConfig = {
            features: {
              qrOrdering: true,
              deliveryEnabled: false,
              reviewsEnabled: true,
              notesEnabled: true
            },
            limits: {
              maxSearchRadius: 50,
              maxFileSize: 5242880, // 5MB
              maxImagesPerReview: 5
            },
            contact: {
              supportEmail: 'support@barclik.app',
              supportPhone: '+34 000 000 000'
            },
            oauth: {
              enabled: false,
              providers: []
            },
            registration: {
              enabled: true,
              email_verification_required: true,
              password_requirements: {
                min_length: 8,
                require_uppercase: true,
                require_lowercase: true,
                require_numbers: true,
                require_special: true
              }
            },
            login: {
              rate_limiting_enabled: true,
              session_management: true
            },
            turnstile: {
              enabled: false,
              siteKey: ''
            }
          };
          
          console.warn('🔧 Usando configuración por defecto debido a error del backend');
          this.configSubject.next(defaultConfig);
          
          return of({
            success: false,
            config: defaultConfig,
            message: 'Error del backend - usando configuración por defecto'
          } as ConfigResponse);
        })
      ).toPromise() as Promise<ConfigResponse>;

    return this.loadingPromise!;
  }

  /**
   * Cargar configuración desde el backend (método público)
   */
  loadConfig(): Observable<ConfigResponse> {
    // Si ya está cargada, devolver inmediatamente
    if (this.configSubject.value) {
      return of({
        success: true,
        config: this.configSubject.value,
        message: 'Configuración ya cargada'
      });
    }

    // Si se está cargando, esperar a que termine
    if (this.loadingPromise) {
      return from(this.loadingPromise);
    }

    // Cargar por primera vez
    return from(this.loadConfigSingleton());
  }

  /**
   * Obtener configuración actual (valor sincrónico)
   */
  getCurrentConfig(): AppConfig | null {
    return this.configSubject.value;
  }

  /**
   * Obtener configuracion de Turnstile
   */
  getTurnstileConfig(): { enabled: boolean; siteKey: string } | null {
    const config = this.getCurrentConfig();
    return config?.turnstile || null;
  }

  /**
   * Obtener configuracion de Mapbox
   */
  getMapboxConfig(): { enabled: boolean; accessToken: string } | null {
    const config = this.getCurrentConfig();
    return config?.mapbox || null;
  }

  /**
   * Verificar si una característica está habilitada
   */
  isFeatureEnabled(feature: keyof AppConfig['features']): boolean {
    const config = this.getCurrentConfig();
    return config?.features[feature] || false;
  }

  /**
   * Obtener límites de la aplicación
   */
  getLimits(): AppConfig['limits'] | null {
    const config = this.getCurrentConfig();
    return config?.limits || null;
  }

  /**
   * Obtener información de contacto
   */
  getContactInfo(): AppConfig['contact'] | null {
    const config = this.getCurrentConfig();
    return config?.contact || null;
  }

  /**
   * Obtener URL base de la API
   */
  getApiUrl(): string {
    // En desarrollo, usar localhost
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8080';
    }
    
    // En producción, usar el mismo host
    return window.location.origin;
  }

  /**
   * Obtener URL base del CDN de media (R2_PUBLIC_URL del backend .env)
   * Lee de la config cargada del backend. Fallback a PRE si no configurado.
   */
  getMediaUrl(): string {
    const config = this.getCurrentConfig();
    const url = config?.media?.baseUrl;
    if (url && url.trim()) {
      return url.replace(/\/+$/, ''); // strip trailing slash
    }
    // Fallback: always PRE until R2_PUBLIC_URL is configured in production .env
    return 'https://media.pre.barclik.com';
  }

  /**
   * Resuelve una URL de imagen: si es relativa, le pone el prefijo del CDN de media.
   * Si ya es absoluta (http/https), la devuelve tal cual.
   */
  resolveImageUrl(url: string | null | undefined): string {
    if (!url || !url.trim()) return '';
    // Already absolute
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // Relative — prepend media base
    const base = this.getMediaUrl();
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  }

  /**
   * Verificar si OAuth está habilitado
   */
  isOAuthEnabled(): boolean {
    const config = this.getCurrentConfig();
    return config?.oauth?.enabled || false;
  }

  /**
   * Obtener proveedores OAuth disponibles
   */
  getOAuthProviders(): OAuthProvider[] {
    const config = this.getCurrentConfig();
    return config?.oauth?.providers || [];
  }

  /**
   * Obtener proveedor OAuth específico por nombre
   */
  getOAuthProvider(name: string): OAuthProvider | null {
    const providers = this.getOAuthProviders();
    return providers.find(provider => provider.name === name) || null;
  }

  /**
   * Verificar si un proveedor OAuth específico está habilitado
   */
  isOAuthProviderEnabled(name: string): boolean {
    const provider = this.getOAuthProvider(name);
    return provider?.enabled || false;
  }

  /**
   * Obtener configuración de registro
   */
  getRegistrationConfig(): AppConfig['registration'] | null {
    const config = this.getCurrentConfig();
    return config?.registration || null;
  }
}